import React, { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { 
  FileText, 
  Download, 
  Save, 
  Check, 
  ChevronRight,
  Eye,
  Target,
  GraduationCap,
  Heart,
  Settings,
  MessageSquare,
  Sparkles,
  Cross,
  Plus
} from "lucide-react";
import { toast } from "sonner";

interface PlanSection {
  id: string;
  title: string;
  content: string;
  subsections?: { title: string; content: string }[];
  isUserProvided?: boolean;
}

interface ExecutiveSummaryItem {
  section: string;
  highlights: string[];
}

export interface LeadershipPlan {
  title: string;
  executiveSummary: ExecutiveSummaryItem[];
  sections: PlanSection[];
  generatedAt: string;
}

interface LeadershipPlanCanvasProps {
  plan: LeadershipPlan | null;
  onSave?: () => void;
  onStartNew?: () => void;
  isSaving?: boolean;
  lastSaved?: Date | null;
}

const SECTION_ICONS: Record<string, React.ReactNode> = {
  "vision-mission": <Eye className="w-5 h-5" />,
  "spiritual-formation": <Cross className="w-5 h-5" />,
  "evangelization": <Sparkles className="w-5 h-5" />,
  "catholic-education": <GraduationCap className="w-5 h-5" />,
  "charitable-outreach": <Heart className="w-5 h-5" />,
  "administrative-excellence": <Settings className="w-5 h-5" />,
  "communication": <MessageSquare className="w-5 h-5" />,
};

const LeadershipPlanCanvas: React.FC<LeadershipPlanCanvasProps> = ({
  plan,
  onSave,
  onStartNew,
  isSaving,
  lastSaved,
}) => {
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  if (!plan) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="text-center space-y-4">
          <FileText className="w-12 h-12 text-muted-foreground mx-auto" />
          <p className="text-muted-foreground">
            Your leadership plan will appear here once generated.
          </p>
        </div>
      </div>
    );
  }

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(`section-${sectionId}`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
      setActiveSection(sectionId);
    }
  };

  const handleDownloadWord = async () => {
    try {
      // Dynamic import for docx library
      const { Document, Packer, Paragraph, HeadingLevel, TextRun } = await import("docx");
      
      const doc = new Document({
        sections: [{
          children: [
            new Paragraph({
              text: plan.title,
              heading: HeadingLevel.TITLE,
            }),
            new Paragraph({
              children: [
                new TextRun({
                  text: `Generated: ${new Date(plan.generatedAt).toLocaleDateString()}`,
                  italics: true,
                }),
              ],
            }),
            new Paragraph({ text: "" }),
            new Paragraph({
              text: "Executive Summary",
              heading: HeadingLevel.HEADING_1,
            }),
            ...plan.executiveSummary.flatMap(item => [
              new Paragraph({
                text: item.section,
                heading: HeadingLevel.HEADING_2,
              }),
              ...item.highlights.map(h => new Paragraph({
                text: `• ${h}`,
                bullet: { level: 0 },
              })),
            ]),
            new Paragraph({ text: "" }),
            ...plan.sections.flatMap(section => [
              new Paragraph({
                text: section.title,
                heading: HeadingLevel.HEADING_1,
              }),
              new Paragraph({ text: section.content }),
              ...(section.subsections?.flatMap(sub => [
                new Paragraph({
                  text: sub.title,
                  heading: HeadingLevel.HEADING_2,
                }),
                new Paragraph({ text: sub.content }),
              ]) || []),
            ]),
          ],
        }],
      });

      const blob = await Packer.toBlob(doc);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${plan.title.replace(/[^a-z0-9]/gi, "_")}.docx`;
      link.click();
      URL.revokeObjectURL(url);
      
      toast.success("Word document downloaded!");
    } catch (error) {
      console.error("Word export error:", error);
      toast.error("Failed to export Word document");
    }
  };

  const handleDownloadPDF = () => {
    // Use browser's native print dialog with print-to-PDF option
    // This avoids the vulnerable html2pdf.js/jspdf dependency
    if (contentRef.current) {
      const printContent = contentRef.current.innerHTML;
      const printWindow = window.open('', '_blank');
      
      if (printWindow) {
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>${plan.title}</title>
              <style>
                body { 
                  font-family: system-ui, -apple-system, sans-serif; 
                  padding: 40px; 
                  max-width: 800px; 
                  margin: 0 auto;
                  line-height: 1.6;
                }
                h1, h2, h3 { margin-top: 1.5em; }
                @media print {
                  body { padding: 20px; }
                }
              </style>
            </head>
            <body>
              ${printContent}
            </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.focus();
        
        // Small delay to ensure content is rendered
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 250);
        
        toast.success("Print dialog opened - select 'Save as PDF' to download");
      } else {
        toast.error("Please allow popups to download PDF");
      }
    }
  };

  return (
    <div className="flex h-full">
      {/* Table of Contents Sidebar */}
      <div className="w-64 border-r border-border bg-card/50 flex flex-col">
        <div className="p-4 border-b border-border">
          <h3 className="font-semibold text-sm text-foreground">Contents</h3>
        </div>
        <ScrollArea className="flex-1">
          <div className="p-2 space-y-1">
            <button
              onClick={() => scrollToSection("executive-summary")}
              className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                activeSection === "executive-summary"
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
              }`}
            >
              <div className="flex items-center gap-2">
                <Target className="w-4 h-4" />
                Executive Summary
              </div>
            </button>
            
            {plan.sections.map(section => (
              <button
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                  activeSection === section.id
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                }`}
              >
                <div className="flex items-center gap-2">
                  {SECTION_ICONS[section.id] || <ChevronRight className="w-4 h-4" />}
                  <span className="line-clamp-1">{section.title}</span>
                </div>
              </button>
            ))}
          </div>
        </ScrollArea>
        
        {/* Action Buttons */}
        <div className="p-4 border-t border-border space-y-2">
          {onStartNew && (
            <Button 
              variant="outline" 
              className="w-full justify-start" 
              onClick={onStartNew}
            >
              <Plus className="w-4 h-4 mr-2" />
              New Plan
            </Button>
          )}
          <Button 
            variant="outline" 
            className="w-full justify-start" 
            onClick={handleDownloadWord}
          >
            <Download className="w-4 h-4 mr-2" />
            Download Word
          </Button>
          <Button 
            variant="outline" 
            className="w-full justify-start"
            onClick={handleDownloadPDF}
          >
            <Download className="w-4 h-4 mr-2" />
            Download PDF
          </Button>
          {onSave && (
            <Button 
              className="w-full justify-start"
              onClick={onSave}
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <Save className="w-4 h-4 mr-2 animate-pulse" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Version
                </>
              )}
            </Button>
          )}
          {lastSaved && (
            <p className="text-xs text-muted-foreground text-center">
              Last saved: {lastSaved.toLocaleTimeString()}
            </p>
          )}
        </div>
      </div>

      {/* Document Content */}
      <ScrollArea className="flex-1">
        <div ref={contentRef} className="max-w-4xl mx-auto p-8 space-y-8">
          {/* Title */}
          <div className="text-center space-y-2">
            <h1 className="text-3xl font-bold font-display text-foreground">
              {plan.title}
            </h1>
            <p className="text-sm text-muted-foreground">
              Generated on {new Date(plan.generatedAt).toLocaleDateString()}
            </p>
          </div>

          <Separator />

          {/* Executive Summary */}
          <section id="section-executive-summary" className="space-y-6">
            <h2 className="text-2xl font-bold font-display text-foreground flex items-center gap-2">
              <Target className="w-6 h-6 text-primary" />
              Executive Summary
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {plan.executiveSummary.map((item, index) => (
                <Card key={index} className="border-l-4 border-l-primary">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">{item.section}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-1">
                      {item.highlights.map((highlight, hIndex) => (
                        <li key={hIndex} className="text-sm text-muted-foreground flex items-start gap-2">
                          <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                          {highlight}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          <Separator />

          {/* Detailed Sections */}
          {plan.sections.map((section, index) => (
            <section 
              key={section.id} 
              id={`section-${section.id}`}
              className="space-y-4"
            >
              <h2 className="text-2xl font-bold font-display text-foreground flex items-center gap-2">
                {SECTION_ICONS[section.id] || <ChevronRight className="w-6 h-6 text-primary" />}
                {section.title}
                {section.isUserProvided && (
                  <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full font-normal">
                    User Provided
                  </span>
                )}
              </h2>
              
              <div className="prose prose-slate dark:prose-invert max-w-none">
                <p className="text-muted-foreground whitespace-pre-wrap">
                  {section.content}
                </p>
              </div>

              {section.subsections && section.subsections.length > 0 && (
                <div className="space-y-4 pl-4 border-l-2 border-border">
                  {section.subsections.map((sub, subIndex) => (
                    <div key={subIndex}>
                      <h3 className="text-lg font-semibold text-foreground mb-2">
                        {sub.title}
                      </h3>
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                        {sub.content}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {index < plan.sections.length - 1 && <Separator className="mt-8" />}
            </section>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
};

export default LeadershipPlanCanvas;